import { useEffect } from 'react';
import { MessageCircle, Phone, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/layout/Layout';


// Store information
const storeInfo = {
  
  phone: '+91 92144 56664',
  phoneDisplay: '+91 92144 56664',
  address: 'D-2, Bhopalpura, Near Gattani Hospital, Udaipur, Rajasthan – 313001',
  city: 'Udaipur, Rajasthan – 313001',
  hours: [
    { day: 'Monday - Saturday', time: '11:00 AM - 9:00 PM' },
    { day: 'Sunday', time: '11:00 AM - 5:00 PM' },
  ],
  mapEmbedUrl: 'https://www.google.com/maps?q=24.5886045,73.7005024&output=embed',
};

export const Contact = () => {
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      'Hello, I would like to enquire about a design from Anand Fashion.'
    );
    window.open(`https://wa.me/919214456664?text=${message}`, '_blank');
  };

  const handlePhoneClick = () => {
    window.location.href = `tel:${storeInfo.phone.replace(/\s/g, '')}`;
  };

  const handleDirectionsClick = () => {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        storeInfo.address + ', ' + storeInfo.city
      )}`,
      '_blank'
    );
  };

  // Intersection Observer for reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const revealElements = document.querySelectorAll('.reveal-section');
    revealElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-subtle">
        <div className="container-heritage text-center">
          <div className="max-w-2xl mx-auto stagger-children">
            <span className="label-uppercase mb-4 block">Get in Touch</span>
            <h1 className="heading-display text-foreground mb-6">
              Visit Our Showroom
            </h1>
            <div className="flex justify-center mb-6">
              <div className="line-gold" />
            </div>
            <p className="body-large">
              Experience our branded collection in person. Our fashion consultants 
              are ready to help you find the perfect style.
            </p>
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="section-padding">
        <div className="container-heritage">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Information */}
            <div className="reveal-section">
              <h2 className="heading-subsection text-foreground mb-8">
                Contact Information
              </h2>
              
              {/* WhatsApp - Primary CTA */}
              <div className="mb-10">
                <Button
                  onClick={handleWhatsAppClick}
                  className="btn-heritage-filled w-full sm:w-auto group whatsapp-pulse"
                  style={{ backgroundColor: 'hsl(142 70% 40%)' }}
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Enquire on WhatsApp
                </Button>
                <p className="text-xs text-muted-foreground mt-3">
                  Fastest way to reach us
                </p>
              </div>

              {/* Contact Details */}
              <div className="space-y-8">
                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-foreground" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-foreground mb-1">
                      Call Us
                    </h3>
                    <button
                      onClick={handlePhoneClick}
                      className="text-muted-foreground hover:text-foreground transition-colors duration-300"
                    >
                      {storeInfo.phoneDisplay}
                    </button>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-foreground" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-foreground mb-1">
                      Visit Us
                    </h3>
                    <p className="text-muted-foreground mb-2">
                      {storeInfo.address}
                      <br />
                      {storeInfo.city}
                    </p>
                    <button
                      onClick={handleDirectionsClick}
                      className="text-sm text-gold-dark hover:text-gold transition-colors duration-300 link-underline"
                    >
                      Get Directions
                    </button>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-foreground" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-foreground mb-2">
                      Business Hours
                    </h3>
                    <div className="space-y-1">
                      {storeInfo.hours.map((schedule, index) => (
                        <div key={index} className="flex justify-between gap-8">
                          <span className="text-muted-foreground">
                            {schedule.day}
                          </span>
                          <span className="text-foreground">
                            {schedule.time}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="reveal-section" style={{ transitionDelay: '0.2s' }}>
              <div className="aspect-square lg:aspect-[4/5] rounded-sm overflow-hidden map-container">
                <iframe
                  src={storeInfo.mapEmbedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Anand Fashion Location"
                  className="w-full h-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="section-padding-sm bg-secondary/30">
        <div className="container-heritage">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {/* Free Consultation */}
            <div className="reveal-section p-6">
              <h3 className="font-serif text-xl text-foreground mb-3">
                Free Style Consultation
              </h3>
              <p className="text-sm text-muted-foreground">
                Our experienced consultants will help you find the perfect 
                outfit for any occasion.
              </p>
            </div>

            {/* Personalized Service */}
            <div className="reveal-section p-6" style={{ transitionDelay: '0.1s' }}>
              <h3 className="font-serif text-xl text-foreground mb-3">
                Personalized Service
              </h3>
              <p className="text-sm text-muted-foreground">
                Experience one-on-one attention in our comfortable, 
                private showroom environment.
              </p>
            </div>

            {/* Alteration Services */}
            <div className="reveal-section p-6" style={{ transitionDelay: '0.2s' }}>
              <h3 className="font-serif text-xl text-foreground mb-3">
                Expert Alterations
              </h3>
              <p className="text-sm text-muted-foreground">
                Our in-house tailors ensure every piece fits you 
                perfectly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding">
        <div className="container-heritage">
          <div className="bg-gradient-warm rounded-sm p-10 md:p-16 text-center reveal-section">
            <h2 className="heading-subsection text-foreground mb-4">
              Ready to Find Your Perfect Style?
            </h2>
            <p className="body-large max-w-lg mx-auto mb-8">
              Whether it's a wedding, festival, or everyday elegance, 
              we're here to help you look your best.
            </p>
            <Button
              onClick={handleWhatsAppClick}
              className="btn-heritage-filled group"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Start Your Style Journey
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
